import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { PayementPer } from 'src/model/model.payementPer';
import { environment } from 'src/environments/environment';
import { Page } from 'src/model/model.page';
import { StatsDepense } from 'src/app/dashboard/depense-dashboard/stats-depense.model';

@Injectable()
export class PayementPerService {
  basePath = environment.BasePath + "/survey";

  constructor(private httpClient: HttpClient) {}

  // Fonction utilitaire pour nettoyer les dates
  private cleanPayementDate(payement: PayementPer): PayementPer {
    const cleanPayement = { ...payement };

    // S'assurer que datePay est une string au format YYYY-MM-DD
    if (cleanPayement.datePay) {
      // Convertir en string si ce n'est pas déjà le cas
      let dateStr = cleanPayement.datePay.toString();

      if (dateStr.includes('T')) {
        // Si c'est une string ISO avec l'heure, extraire seulement la date
        cleanPayement.datePay = dateStr.split('T')[0];
      } else if (dateStr.includes('/')) {
        // Si c'est au format DD/MM/YYYY, convertir en YYYY-MM-DD
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parts[0].padStart(2, '0');
          const month = parts[1].padStart(2, '0');
          const year = parts[2];
          cleanPayement.datePay = `${year}-${month}-${day}`;
        }
      } else {
        // Essayer de créer un objet Date et le formater
        try {
          const dateObj = new Date(dateStr);
          if (!isNaN(dateObj.getTime())) {
            const year = dateObj.getFullYear();
            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
            const day = dateObj.getDate().toString().padStart(2, '0');
            cleanPayement.datePay = `${year}-${month}-${day}`;
          }
        } catch (e) {
          console.error('Erreur lors de la conversion de date:', e);
        }
      }
    }

    return cleanPayement;
  }

  getPaginatedPayements(
    idEtab: number,
    page: number,
    size: number,
    sujetDon?: string,
    mois?: string,
    mantant?: string,
    datePay?: string,
    paymentReference?: string // Replace idPay
  ): Observable<Page<PayementPer>> {
    if (!idEtab) {
      throw new Error('idEtab is required for paginated request');
    }
    let url = `${this.basePath}/payementPer/paginated?page=${page}&size=${size}`;
    const params: any = {};
    if (sujetDon) params.sujetDon = sujetDon;
    if (mois) params.remarque = mois; // "mois" corresponds to "remarque" on the backend
    if (mantant) params.mantant = mantant;
    if (datePay) params.datePay = datePay;
    if (paymentReference) params.paymentReference = paymentReference; // Replace idPay

    return this.httpClient.get<Page<PayementPer>>(url, {
      headers: { 'idEtab': idEtab.toString() },
      params: params
    });
  }

  getTotalAmount(
    idEtab: number,
    sujetDon?: string,
    mois?: string,
    mantant?: string,
    datePay?: string,
    idPay?: string // New parameter for idPay filtering in total
  ): Observable<number> {
    if (!idEtab) {
      throw new Error('idEtab is required for total amount request');
    }
    let url = `${this.basePath}/payementPer/total`;
    const params: any = {};
    if (sujetDon) params.sujetDon = sujetDon;
    if (mois) params.remarque = mois; // "mois" corresponds to "remarque" on the backend
    if (mantant) params.mantant = mantant;
    if (datePay) params.datePay = datePay;
    if (idPay) params.idPay = idPay; // Add idPay to query parameters

    return this.httpClient.get<number>(url, {
      headers: { 'idEtab': idEtab.toString() },
      params: params
    });
  }

  getPayements(motCle: string): Observable<any> {
    return this.httpClient.get(this.basePath + "/cherchePayementPer?nom=" + motCle);
  }

  getAllPayements(motCle: string, size: number, page: number): Observable<any> {
    return this.httpClient.get(this.basePath + "/chercheAllPayementPer?nom=" + motCle + "&size=" + size);
  }

  findAllPayementsPer(): Observable<any> {
    return this.httpClient.get(this.basePath + "/findAllPayementPer");
  }

  getSortie(motCle: string): Observable<any> {
    return this.httpClient.get(this.basePath + "/chercheSortie?nom=" + motCle);
  }

  getPaginatedPayementsByPersonnel(
    personnelId: number,
    idEtab: number,
    page: number,
    size: number,
    sujetDon?: string,
    remarque?: string,
    mantant?: string,
    datePay?: string,
    typeKafaala?: string,
    nomKafil?: string,
    paymentReference?: string, // Replace idPay
    typePaiement?: string
  ): Observable<{
    content: PayementPer[];
    pageable: any;
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
    totalMantant: number;
  }> {
    if (!personnelId || !idEtab) {
      throw new Error('personnelId and idEtab are required');
    }

    let url = `${this.basePath}/payement/personnel/${personnelId}/paginated?page=${page}&size=${size}`;
    const params: any = {};
    if (sujetDon) params.sujetDon = sujetDon;
    if (remarque) params.remarque = remarque;
    if (mantant) params.mantant = mantant;
    if (datePay) params.datePay = datePay;
    if (typeKafaala) params.typeKafaala = typeKafaala;
    if (nomKafil) params.nomKafil = nomKafil;
    if (paymentReference) params.paymentReference = paymentReference; // Replace idPay
    if (typePaiement) params.typePaiement = typePaiement;

    return this.httpClient.get<any>(url, {
      headers: { 'idEtab': idEtab.toString() },
      params
    }).pipe(
      map(response => ({
        content: response.content || [],
        pageable: {
          offset: (response.number || page) * (response.size || size),
          pageNumber: response.number || page,
          pageSize: response.size || size,
          paged: true,
          unpaged: false
        },
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0,
        size: response.size || size,
        number: response.number || page,
        numberOfElements: response.numberOfElements || 0,
        first: response.first || false,
        last: response.last || false,
        empty: response.empty || true,
        totalMantant: response.totalMantant || 0
      })),
      catchError((error) => {
        console.error('Error in getPaginatedPayementsByPersonnel:', error);
        return throwError(error);
      })
    );
  }

  getPayementById(id: number): Observable<any> {
    return this.httpClient.get(this.basePath + "/payementPer/" + id);
  }

  deletePayement(id: number): Observable<any> {
    return this.httpClient.delete(`${this.basePath}/payementPer/${id}`, { responseType: 'text' });
  }

  editPayement(payement: PayementPer): Observable<any> {
    console.log('=== SERVICE EDIT PAYEMENT ===');
    console.log('Payement reçu (avant intercepteur):', payement);
    console.log('datePay original:', payement.datePay);
    console.log('Type de datePay:', typeof payement.datePay);
    console.log('L\'intercepteur va nettoyer automatiquement les dates');
    console.log('=== FIN SERVICE EDIT ===');

    // L'intercepteur va automatiquement nettoyer les dates
    return this.httpClient.put(this.basePath + "/payementPer/" + payement.idPay, payement);
  }

  savePayement(payement: PayementPer): Observable<any> {
    console.log('=== SERVICE SAVE PAYEMENT ===');
    console.log('Payement reçu:', payement);
    console.log('datePay original:', payement.datePay);
    console.log('Type de datePay:', typeof payement.datePay);

    const cleanPayement = this.cleanPayementDate(payement);

    console.log('Payement nettoyé:', cleanPayement);
    console.log('datePay final:', cleanPayement.datePay);
    console.log('Type final:', typeof cleanPayement.datePay);
    console.log('=== FIN SERVICE ===');

    return this.httpClient.post(this.basePath + "/payementPer", cleanPayement);
  }
// Nouvelle méthode pour les stats des aides aux familles
  getStatsAidesFamilles(idEtab: number): Observable<StatsDepense[]> {
    if (!idEtab) {
      throw new Error('idEtab is required for stats request');
    }
    const headers = new HttpHeaders({
      'idEtab': idEtab.toString()
    });
    return this.httpClient.get<StatsDepense[]>(`${this.basePath}/stats/aides-familles`, { headers }).pipe(
      map(data => data.map(item => new StatsDepense(item))),
      catchError((error) => {
        console.error('Error loading stats aides familles:', error);
        return throwError(error);
      })
    );
  }
  
}
